/**
 * content/index.js - Isaac No Youtube's declaration, and the promise made
 * checkable.
 *
 * A game plugs into the GAME-LEVEL-ENGINE by declaring what it covers and what
 * it can measure, and then never contradicting the engine about anything else.
 *
 * The SHARED checks - the ones every game must pass - are
 * client/test/support/contractChecks.js and run against this game the moment it
 * is in client/test/support/manifests.js. What is left here is the handful this
 * game has to check for itself, and the one nobody else could: that the word
 * this game exists to quieten never becomes a word it drills.
 */

import {
  getLevel,
  getPromptStyle,
  getBoxesForLevel,
  getStrictness,
  SUPPORT_BY_ID,
  CAPABILITY_IDS,
  validateCapabilities,
  validateCoverage,
} from '../../../../engine/framework.js';
import { gameOwedCodes, engineJudgedCodes } from '../../../../engine/contract.js';

import { LEVELS, COVERAGE, getLevelContent, pacingFor } from './levels.js';
import { NEARBY, NEVER_A_TARGET, getNearby } from './nearby.js';
import { PRAISE, ENCOURAGEMENT, NEARLY, ASSIST, PROMPTS, HINTS, LEVEL_INTRO } from './voice.js';

/**
 * The game, as the engine sees it.
 *
 * `capabilities` is a MIRROR, not the source of truth: the server reads
 * games.engine_capabilities from the registry, deliberately, so a client can
 * never assert what it instruments. This copy exists so the game knows which
 * trial fields it is obliged to fill, and
 * database/register_isaac-no-youtube.sql must agree with it. A test asserts it.
 *
 * WHY audio_energy AND NOT prompt_sequence. Of the fifteen boxes in this game's
 * coverage, five are observational (Attends to Model - a microphone cannot see
 * whether a child is looking), five are Attempts Vocalization, which the ENGINE
 * judges from the audio envelope and which needs `audio_energy`, and five are
 * Produces Target, which this game judges from the transcript and needs no
 * capability at all. `prompt_sequence` unlocks nothing at Phase 1: no box here
 * depends on where in the prompt sequence the child spoke, so declaring it
 * would be claiming an instrument that buys no measurement. Door Knock declines
 * audio_energy for the mirror-image reason.
 */
export const GAME = Object.freeze({
  id: 'isaac-no-youtube',
  name: 'Isaac No Youtube',
  description:
    'A screen is glowing, and the room is full of people. Say who is there and '
    + 'they come to you. Phase 1: echoic single words, five levels, from one '
    + 'person on the sofa to everybody at the park.',
  phasesAvailable: COVERAGE.phasesAvailable,
  levelsAvailable: Object.freeze(COVERAGE.levelsAvailable),
  capabilities: Object.freeze({ prompt_sequence: false, audio_energy: true }),
  minAge: 2,
  maxAge: 8,
  skillTags: Object.freeze(['speech', 'vocabulary', 'early-words', 'attention']),
  totalLevels: LEVELS.length,
});

/** The four pools, in the shape shared/lineBag.js consumes. */
export const POOLS = Object.freeze({
  praise: PRAISE,
  encouragement: ENCOURAGEMENT,
  nearly: NEARLY,
  assist: ASSIST,
});

/**
 * @returns {{ok:boolean, errors:string[], warnings:string[], summary:object}}
 */
export function validateContent() {
  const errors = [];
  const warnings = [];

  // ---- the declaration is one the engine recognises ----------------------
  const caps = validateCapabilities(GAME.capabilities);
  if (!caps.ok) errors.push(...caps.errors);

  const coverage = validateCoverage({
    phasesAvailable: GAME.phasesAvailable,
    levelsAvailable: GAME.levelsAvailable,
  });
  if (!coverage.ok) errors.push(...coverage.errors);
  if (coverage.ok && coverage.availableLevels.length !== LEVELS.length) {
    errors.push(
      `Coverage declares ${coverage.availableLevels.length} levels but the content has `
      + `${LEVELS.length}. A level the engine believes in and the game cannot load is `
      + 'exactly what levelsAvailable exists to prevent.'
    );
  }

  // ---- THE RULE THIS GAME EXISTS FOR -------------------------------------
  // The thing Isaac is being helped away from must never become a word he is
  // asked to say. A Phase 1 target is spoken aloud by Pip and repeated by the
  // child ten or more times per box, and a game that drilled "youtube" would be
  // teaching the fixation it was written to quieten. Scenery only - see
  // content/nearby.js.
  for (const item of Object.values(NEARBY)) {
    for (const candidate of [item.word, ...item.accept]) {
      if (NEVER_A_TARGET.includes(candidate.toLowerCase())) {
        errors.push(
          `"${candidate}" is a target word in this game. The screen is scenery here: `
          + 'it is drawn, it dims, and it is never named. Adding it to the catalogue '
          + 'would drill the word the game exists to quieten.'
        );
      }
    }
  }
  for (const [phase, byRung] of Object.entries(PROMPTS)) {
    for (const lines of Object.values(byRung)) {
      for (const line of lines) {
        for (const term of NEVER_A_TARGET) {
          if (new RegExp(`\\b${term}\\b`, 'i').test(line)) {
            errors.push(`Phase ${phase} prompt "${line}" asks the child for "${term}".`);
          }
        }
      }
    }
  }

  // ---- every level exists in the framework, at the right phase -----------
  const seen = new Set();
  for (const content of LEVELS) {
    const where = `${content.id} (phase ${content.phase} level ${content.level})`;

    if (!GAME.phasesAvailable.includes(content.phase)) {
      errors.push(`${where}: phase is not in phasesAvailable [${GAME.phasesAvailable}].`);
    }
    if (!getLevel(content.phase, content.level)) {
      errors.push(`${where}: no such level in the framework.`);
      continue;
    }

    const key = `${content.phase}.${content.level}`;
    if (seen.has(key)) errors.push(`${where}: two content entries claim the same framework level.`);
    seen.add(key);

    // ---- the opening rung is inside the phase's own bounds ---------------
    // Lower ladder index = MORE support. At Phase 1 the bounds are 0..0, so
    // this is really an assertion that the level opens at the full model.
    const rung = SUPPORT_BY_ID[content.nominalRung];
    if (!rung) {
      errors.push(`${where}: nominalRung "${content.nominalRung}" is not a SUPPORT_LADDER rung.`);
    } else {
      const { maxSupport, minSupport } = getPromptStyle(content.phase).support;
      if (rung.index < maxSupport || rung.index > minSupport) {
        errors.push(
          `${where}: nominalRung "${content.nominalRung}" (index ${rung.index}) is outside `
          + `phase ${content.phase}'s bounds ${maxSupport}..${minSupport}.`
        );
      }
    }

    // ---- there are words for the rung it opens at ------------------------
    const byRung = PROMPTS[content.phase] || {};
    if (!byRung[content.nominalRung] || !byRung[content.nominalRung].length) {
      errors.push(`${where}: no prompt lines for the opening rung "${content.nominalRung}".`);
    }

    // ---- the pool is real, and wide enough not to recite itself ----------
    if (!content.pool.length) errors.push(`${where}: empty pool.`);
    for (const id of content.pool) {
      if (!getNearby(id)) errors.push(`${where}: unknown id "${id}".`);
    }
    if (content.pool.length < content.targetsPerRound * 3) {
      errors.push(
        `${where}: a pool of ${content.pool.length} will recite itself before a box has `
        + 'ten plays.'
      );
    }
    if (content.fieldSize < content.targetsPerRound) {
      errors.push(`${where}: fieldSize (${content.fieldSize}) is fewer than targetsPerRound.`);
    }
    if (content.fieldSize > content.pool.length) {
      errors.push(
        `${where}: fieldSize (${content.fieldSize}) exceeds the pool `
        + `(${content.pool.length}); the field cannot be filled without a duplicate.`
      );
    }

    // ---- the level narrates itself ---------------------------------------
    if (!content.opening || !content.opening.length) {
      errors.push(`${where}: no opening lines. The arc across the five levels is the game.`);
    }
    if (!['bright', 'fading', 'out'].includes(content.glow)) {
      errors.push(`${where}: glow "${content.glow}" is not one of bright, fading, out.`);
    }

    // ---- pacing is ordered and fits inside the response window -----------
    const pacing = pacingFor(content);
    const escalations = pacing.escalateAtMs || [];
    for (let i = 1; i < escalations.length; i += 1) {
      if (escalations[i] <= escalations[i - 1]) {
        errors.push(`${where}: escalateAtMs is not strictly increasing.`);
      }
    }
    if (escalations.length && escalations[escalations.length - 1] >= pacing.windowMs) {
      errors.push(`${where}: the last escalation would never be seen.`);
    }
    if (pacing.hintAtMs >= pacing.windowMs) {
      errors.push(`${where}: hintAtMs is at or after the window closes.`);
    }

    if (!HINTS[content.phase] || !HINTS[content.phase].length) {
      errors.push(`${where}: no hint lines for phase ${content.phase}.`);
    }
    if (!LEVEL_INTRO[content.phase] || !LEVEL_INTRO[content.phase].length) {
      errors.push(`${where}: no level intro lines for phase ${content.phase}.`);
    }
  }

  // ---- section 8.2: Phase 1 says the word out loud, every time -----------
  for (const line of PROMPTS[1].full_model) {
    if (!line.includes('{word}')) {
      errors.push(`Phase 1 prompt "${line}" does not speak the target word (section 8.2).`);
    }
  }

  // ---- a clue never contains its own answer ------------------------------
  for (const item of Object.values(NEARBY)) {
    if (item.clue.toLowerCase().includes(item.word.toLowerCase())) {
      errors.push(`The clue for "${item.word}" contains the word itself; that is a model, not a clue.`);
    }
  }

  // ---- one transcript must never score two different targets -------------
  const owner = new Map();
  for (const item of Object.values(NEARBY)) {
    for (const word of item.accept) {
      if (owner.has(word)) {
        errors.push(`"${word}" is accepted by both ${owner.get(word)} and ${item.id}.`);
      }
      owner.set(word, item.id);
    }
    if (!item.accept.includes(item.word)) errors.push(`"${item.word}" does not accept itself.`);
  }

  // ---- section 8.5: 100+ unique praise phrases ---------------------------
  const praise = PRAISE.map((p) => p.text);
  if (praise.length < 100) errors.push(`Only ${praise.length} praise phrases; section 8.5 requires 100+.`);
  if (new Set(praise).size !== praise.length) errors.push('Duplicate praise phrases.');

  // ---- warnings ----------------------------------------------------------
  // Not an error, because the framework does not require it and a future level
  // might legitimately be all people. It is worth knowing all the same: the
  // whole content argument of this game is that the alternative to a screen is
  // other people and the things that need them.
  const together = Object.values(NEARBY).filter((n) => n.kind === 'together').length;
  if (together < 4) warnings.push('Fewer than four "things you do with somebody" in the catalogue.');

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    summary: {
      levels: LEVELS.length,
      phases: [...new Set(LEVELS.map((l) => l.phase))],
      words: Object.keys(NEARBY).length,
      praise: praise.length,
      encouragement: ENCOURAGEMENT.length,
      nearly: NEARLY.length,
      assist: ASSIST.length,
      capabilities: CAPABILITY_IDS.filter((id) => GAME.capabilities[id]),
      boxesPerLevel: LEVELS.map((l) => ({
        level: `${l.phase}.${l.level}`,
        boxes: getBoxesForLevel(l.phase, l.level).map((b) => b.code),
        gameJudges: gameOwedCodes(l.phase, l.level, GAME.capabilities),
        engineJudges: engineJudgedCodes(l.phase, l.level, GAME.capabilities),
        strictness: getStrictness(l.phase).id,
      })),
    },
  };
}

export { LEVELS, COVERAGE, getLevelContent, pacingFor } from './levels.js';
export { NEARBY, getNearby, canonicalPhrase, acceptedPhrases } from './nearby.js';
export * as VOICE from './voice.js';

export default { GAME, POOLS, validateContent };
